/**
 * <hero-carousel> — split hero (fixed content + sliding image column).
 *
 * Progressive enhancement: the first slide is fully rendered in HTML and is
 * the LCP element. This script only enhances — sliding the image track,
 * autoplay with a progress pill, and a gradient-mask "melt" reveal for the
 * heading / subheading / CTA. With GSAP absent or JS disabled, the first
 * slide still shows and the CTA still links.
 *
 * Dependencies: GSAP core (assets/gsap.min.js). It is NOT loaded up front — the
 * element lazy-loads it (via data-gsap-src) the first time the hero nears the
 * viewport, and skips the download entirely for single-slide heroes and
 * reduced-motion users. So GSAP never blocks rendering and is often never
 * fetched at all.
 */

const REVEAL = { duration: 0.9, stagger: 0.12, ease: 'power2.out' };
const COLOR_VARS = [
  '--hero-bg',
  '--hero-heading-color',
  '--hero-text-color',
  '--hero-button-bg',
  '--hero-button-text',
];

class HeroCarousel extends HTMLElement {
  #index = 0;
  #count = 0;
  #autoplay = false;
  #loop = false;
  #paused = false;
  #hoverPaused = false;
  #swipePaused = false;
  #elapsedAtPause = 0;
  #interval = 8000;
  #timer = null;
  #progressAnim = null;
  #progressStart = 0;
  #reduceMotion = false;
  #revealTl = null;
  #buttonTl = null;
  #hasGsap = false;
  #observer = null;
  #track = null;
  #lastTouchX = 0;
  #slides = [];
  #panels = [];
  #dots = [];
  #toggle = null;
  #button = null;
  #buttonLabel = null;

  connectedCallback() {
    if (this.dataset.ready) return;

    this.#track = this.querySelector('.hero-carousel__track');
    this.#slides = [...this.querySelectorAll('.hero-carousel__slide')];
    this.#panels = [...this.querySelectorAll('.hero-carousel__panel')];
    this.#dots = [...this.querySelectorAll('.hero-carousel__dot')];
    this.#toggle = this.querySelector('.hero-carousel__toggle');
    this.#button = this.querySelector('.hero-carousel__button');
    this.#buttonLabel = this.querySelector('.hero-carousel__button-label');

    this.#count = this.#slides.length;
    if (this.#count === 0) return;

    this.#interval = Number(this.dataset.interval) || 8000;
    this.#autoplay = this.dataset.autoplay === 'true' && this.#count > 1;
    this.#loop = this.dataset.loop === 'true' && this.#count > 1;
    this.#reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // A single-slide hero or a reduced-motion user never animates, so skip the
    // GSAP download entirely and run the static base behaviour immediately.
    const needsGsap = this.#count > 1 && !this.#reduceMotion;
    if (!needsGsap) {
      this.#hasGsap = false;
      this.#start();
      return;
    }

    this.#whenGsapReady(() => {
      this.#hasGsap = typeof gsap !== 'undefined';
      this.#start();
    });
  }

  disconnectedCallback() {
    this.#stopAutoplay();
    this.#observer?.disconnect();
    this.#revealTl?.kill();
    this.#buttonTl?.kill();
  }

  // GSAP is not eagerly loaded. Fetch it lazily the first time the hero is at
  // (or near) the viewport, then run. If GSAP fails to load, run anyway — the
  // carousel works without it (instant transitions). rootMargin pre-warms the
  // download just before the hero scrolls in so the first transition is ready.
  #whenGsapReady(run) {
    if (typeof gsap !== 'undefined') {
      run();
      return;
    }

    const load = () => {
      this.#loadGsap().finally(run);
    };

    if (!('IntersectionObserver' in window)) {
      load();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.#observer.disconnect();
        this.#observer = null;
        load();
      },
      { rootMargin: '200px' }
    );
    this.#observer.observe(this);
  }

  #loadGsap() {
    if (typeof gsap !== 'undefined') return Promise.resolve();

    const src = this.dataset.gsapSrc;
    if (!src) return Promise.resolve();

    // Reuse an in-flight/loaded tag if another hero instance already started it.
    const existing = document.querySelector(`script[data-gsap-loader="${src}"]`);
    if (existing) {
      return existing.dataset.loaded
        ? Promise.resolve()
        : new Promise((resolve) => existing.addEventListener('load', resolve, { once: true }));
    }

    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.dataset.gsapLoader = src;
      script.addEventListener('load', () => {
        script.dataset.loaded = 'true';
        resolve();
      }, { once: true });
      script.addEventListener('error', () => resolve(), { once: true });
      document.head.appendChild(script);
    });
  }

  #start() {
    this.dataset.ready = 'true';
    this.#bindDots();
    this.#bindToggle();
    this.#bindButtonHover();
    this.#bindSwipe();

    // The first slide is already visible/active in markup; just play its
    // reveal and arm autoplay.
    this.#syncVideos();
    this.#playReveal(this.#panels[0]);
    if (this.#autoplay) this.#startAutoplay();
  }

  /* ── Navigation ─────────────────────────────────────────────────────── */

  // driveTrack=false leaves the image track alone (used by the swipe handoff,
  // which is already animating the track in pixels from the released finger
  // position) and only updates the content model: panels, dots, colours,
  // reveal, CTA and the wrapper-height FLIP.
  #goTo(next, { driveTrack = true } = {}) {
    if (next === this.#index || this.#count < 2) return;
    const panel = this.#panels[next];

    // Slide the image track via transform (compositor-friendly). Promote the
    // track to its own layer only for the duration of the tween, then release
    // it so an idle hero doesn't hold a permanent extra layer in memory.
    if (driveTrack) {
      if (this.#hasGsap) {
        this.#track.classList.add('is-sliding');
        gsap.to(this.#track, {
          xPercent: -100 * next,
          duration: 0.7,
          ease: 'power3.inOut',
          force3D: true,
          onComplete: () => this.#track.classList.remove('is-sliding'),
        });
      } else {
        this.#track.style.transform = `translate3d(${-100 * next}%, 0, 0)`;
      }
    }

    // FLIP the panels wrapper height so short/tall headings animate smoothly
    // instead of jumping. Measure old height, swap the active panel, measure
    // the new height, then tween between them and release to auto.
    const wrap = panel.parentElement;
    const fromHeight = this.#hasGsap && wrap ? wrap.offsetHeight : 0;

    this.#setActivePanel(next);
    this.#setActiveDot(next);
    this.#applyColors(panel);
    this.#index = next;
    this.#syncVideos();

    if (this.#hasGsap && wrap) {
      const toHeight = wrap.offsetHeight;
      if (fromHeight !== toHeight) {
        wrap.classList.add('is-resizing');
        wrap.style.willChange = 'height';
        gsap.fromTo(
          wrap,
          { height: fromHeight },
          {
            height: toHeight,
            duration: 0.5,
            ease: 'power3.out',
            onComplete: () => {
              gsap.set(wrap, { clearProps: 'height' });
              wrap.style.willChange = 'auto';
              wrap.classList.remove('is-resizing');
            },
          }
        );
      }
    }

    this.#playReveal(panel);
    this.#morphButton(panel);

    if (this.#autoplay && !this.#paused) this.#restartProgress();
  }

  #next() {
    this.#goTo((this.#index + 1) % this.#count);
  }

  // Step relative to the current slide, honouring loop (wraps around) vs.
  // non-loop (clamps at the ends). Used by touch swipe.
  #step(dir) {
    if (this.#count < 2) return;
    let next = this.#index + dir;
    if (this.#loop) {
      next = (next + this.#count) % this.#count;
    } else {
      next = Math.max(0, Math.min(this.#count - 1, next));
    }
    if (next === this.#index) return;
    this.#goTo(next);
    if (this.#autoplay && !this.#paused) this.#restartProgress();
  }

  #setActivePanel(next) {
    for (const [i, panel] of this.#panels.entries()) {
      const active = i === next;
      panel.classList.toggle('is-active', active);
      panel.toggleAttribute('aria-hidden', !active);
    }
  }

  #setActiveDot(next) {
    for (const [i, dot] of this.#dots.entries()) {
      const active = i === next;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', String(active));
    }
  }

  #applyColors(panel) {
    for (const name of COLOR_VARS) {
      const value = panel.style.getPropertyValue(name);
      if (value) this.style.setProperty(name, value);
    }
  }

  #syncVideos() {
    for (const [i, slide] of this.#slides.entries()) {
      const video = slide.querySelector('video');
      if (!video) continue;
      video.loop = true;

      if (i === this.#index) {
        video.play().catch(() => {});
      } else {
        video.pause();
        video.currentTime = 0;
      }
    }
  }

  /* ── Reveal animation ───────────────────────────────────────────────── */

  #playReveal(panel) {
    if (!panel) return;
    const targets = [
      panel.querySelector('.hero-carousel__heading'),
      panel.querySelector('.hero-carousel__text'),
      panel.querySelector('.hero-carousel__usps'),
    ].filter(Boolean);
    if (targets.length === 0) return;

    if (!this.#hasGsap) {
      for (const el of targets) el.style.setProperty('--reveal', '1');
      return;
    }

    // Toggle the compositor hint on the panel only while the reveal runs.
    panel.classList.add('is-revealing');

    this.#revealTl?.kill();
    this.#revealTl = gsap.timeline({
      defaults: { ease: REVEAL.ease },
      onComplete: () => panel.classList.remove('is-revealing'),
    }).fromTo(
      targets,
      { '--reveal': 0 },
      { '--reveal': 1, duration: REVEAL.duration, stagger: REVEAL.stagger }
    );
  }

  /* ── CTA: melt-swap label + width morph, on the shared rhythm ───────── */

  #morphButton(panel) {
    const button = this.#button;
    const label = this.#buttonLabel;
    if (!button || !label) return;

    const nextLabel = panel.dataset.buttonLabel || '';
    const nextLink = panel.dataset.buttonLink || '';

    if (!nextLabel) {
      button.hidden = true;
      return;
    }
    button.hidden = false;

    const setLink = () => {
      if (nextLink) {
        button.setAttribute('href', nextLink);
        button.removeAttribute('aria-disabled');
      } else {
        button.removeAttribute('href');
        button.setAttribute('aria-disabled', 'true');
      }
    };

    // The link can differ between slides even when the label text is identical,
    // so always sync the href; only the label/width morph below is skippable.
    setLink();
    if (label.textContent === nextLabel) return;

    if (!this.#hasGsap) {
      label.textContent = nextLabel;
      setLink();
      return;
    }

    // Measure both widths in a single read/write group to avoid layout
    // thrashing: read current width, write the new label + auto width, read the
    // target width, then restore. GSAP queues the writes so the browser does
    // one forced reflow for this measure pass rather than interleaving many.
    const startWidth = button.getBoundingClientRect().width;
    const prevText = label.textContent;
    label.textContent = nextLabel;
    gsap.set(button, { width: 'auto' });
    const endWidth = button.getBoundingClientRect().width;
    label.textContent = prevText;
    gsap.set(button, { width: startWidth });

    const d = REVEAL.duration;
    this.#buttonTl?.kill();
    this.#buttonTl = gsap.timeline({
      defaults: { ease: REVEAL.ease },
      onStart: () => {
        button.style.willChange = 'width';
        label.classList.add('is-revealing');
      },
      onComplete: () => {
        button.style.willChange = 'auto';
        label.classList.remove('is-revealing');
      },
    })
      .fromTo(label, { '--reveal': 1 }, { '--reveal': 0, duration: d * 0.4, ease: 'power2.in' }, 0)
      .to(button, { width: endWidth, duration: d }, 0)
      .add(() => {
        label.textContent = nextLabel;
        setLink();
        gsap.set(label, { '--reveal': 0 });
      }, d * 0.4)
      .to(label, { '--reveal': 1, duration: d * 0.55 }, d * 0.42)
      .set(button, { clearProps: 'width' });
  }

  /* ── Autoplay + progress ────────────────────────────────────────────── */

  #startAutoplay() {
    this.#paused = false;
    this.#restartProgress();
  }

  #stopAutoplay() {
    window.clearTimeout(this.#timer);
    // Pausing (not cancelling) keeps the ring frozen where it stopped.
    this.#progressAnim?.pause();
    this.#timer = null;
  }

  #restartProgress() {
    this.#stopAutoplay();
    this.#elapsedAtPause = 0;
    this.#timer = window.setTimeout(() => this.#next(), this.#interval);
    this.#progressStart = performance.now();
    this.#runProgress(0);
  }

  // Pause the countdown but remember how far it had progressed, so resume picks
  // up where it left off instead of restarting the full interval.
  #freezeProgress() {
    this.#elapsedAtPause = Math.min(
      performance.now() - this.#progressStart,
      this.#interval
    );
    this.#stopAutoplay();
  }

  #resumeProgress() {
    this.#stopAutoplay();
    const remaining = Math.max(this.#interval - this.#elapsedAtPause, 0);
    this.#timer = window.setTimeout(() => this.#next(), remaining);
    // Backdate the start so the progress ring continues from the frozen point.
    this.#progressStart = performance.now() - this.#elapsedAtPause;
    this.#runProgress(this.#elapsedAtPause);
  }

  // Sweep the ring from `elapsed` to the end of the interval with a single
  // Web Animation, so no script or style recalc runs per frame.
  #runProgress(elapsed) {
    const ring = this.#toggle?.querySelector('.hero-carousel__ring-progress');
    if (!ring) return;
    const circumference = 94.25;
    const from = circumference * (1 - Math.min(elapsed / this.#interval, 1));
    this.#progressAnim?.cancel();
    this.#progressAnim = null;
    if (typeof ring.animate !== 'function') {
      ring.style.strokeDashoffset = '0';
      return;
    }
    this.#progressAnim = ring.animate(
      [{ strokeDashoffset: String(from) }, { strokeDashoffset: '0' }],
      { duration: Math.max(this.#interval - elapsed, 0), easing: 'linear', fill: 'forwards' }
    );
  }

  /* ── Bindings ───────────────────────────────────────────────────────── */

  #bindDots() {
    for (const dot of this.#dots) {
      dot.addEventListener('click', () => {
        this.#goTo(Number(dot.dataset.index));
        if (this.#autoplay && !this.#paused) this.#restartProgress();
      });
    }
  }

  #bindToggle() {
    const toggle = this.#toggle;
    if (!toggle) return;
    const pauseIcon = toggle.querySelector('.hero-carousel__icon--pause');
    const playIcon = toggle.querySelector('.hero-carousel__icon--play');

    // SVG elements don't reflect .hidden to the [hidden] attribute, so set it.
    const showIcon = (playing) => {
      pauseIcon?.toggleAttribute('hidden', !playing);
      playIcon?.toggleAttribute('hidden', playing);
    };
    showIcon(true);

    toggle.addEventListener('click', () => {
      this.#paused = !this.#paused;
      toggle.setAttribute('data-playing', String(!this.#paused));
      showIcon(!this.#paused);
      if (this.#paused) {
        this.#freezeProgress();
      } else if (!this.#hoverPaused) {
        // Don't resume if the pointer is still resting on the CTA.
        this.#resumeProgress();
      }
    });
  }

  // Pause autoplay while the pointer rests on the CTA, so the slide doesn't
  // change out from under someone about to click it. Resume on leave — but
  // only if the user hasn't explicitly paused via the toggle.
  #bindButtonHover() {
    const button = this.#button;
    if (!button || !this.#autoplay) return;

    button.addEventListener('pointerenter', () => {
      this.#hoverPaused = true;
      this.#freezeProgress();
    });

    button.addEventListener('pointerleave', () => {
      this.#hoverPaused = false;
      if (!this.#paused) this.#resumeProgress();
    });
  }

  // Touch swipe: let mobile users drag the image track to change slides
  // instead of waiting for autoplay. The image track follows the finger 1:1;
  // on release, a swipe past the threshold (distance or velocity) advances,
  // otherwise it snaps back to the current slide. Works with or without GSAP.
  #bindSwipe() {
    const media = this.querySelector('.hero-carousel__media');
    if (!media || this.#count < 2) return;

    let startX = 0;
    let startY = 0;
    let startTime = 0;
    let width = 0;
    let basePx = 0; // resting pixel offset of the current slide (-index * width)
    let currentPx = 0; // pixel offset written on the last frame
    let dragging = false; // a horizontal swipe is in progress
    let decided = false; // axis (horizontal vs. vertical) has been locked in
    let pendingPx = null; // px queued for the next rAF write (drag batching)
    let rafId = null;

    const reset = () => {
      dragging = false;
      decided = false;
      pendingPx = null;
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
    };

    // Drag rendering: write the raw translate once per frame. We bypass GSAP
    // here on purpose — gsap.set() per touchmove re-reads layout to resolve
    // xPercent, thrashing on the main thread. A plain pixel translate3d is the
    // cheapest possible write and never reads back, so the finger-follow stays
    // pinned to 60/120fps.
    const flush = () => {
      rafId = null;
      if (pendingPx === null) return;
      currentPx = pendingPx;
      pendingPx = null;
      this.#track.style.transform = `translate3d(${currentPx}px, 0, 0)`;
    };

    media.addEventListener(
      'touchstart',
      (e) => {
        if (e.touches.length !== 1) return;
        const touch = e.touches[0];
        startX = touch.clientX;
        startY = touch.clientY;
        startTime = performance.now();
        width = media.offsetWidth || this.offsetWidth;
        basePx = -this.#index * width;
        currentPx = basePx;
        decided = false;
        dragging = false;
      },
      { passive: true }
    );

    media.addEventListener(
      'touchmove',
      (e) => {
        if (e.touches.length !== 1 || width === 0) return;
        const touch = e.touches[0];
        this.#lastTouchX = touch.clientX;
        const dx = touch.clientX - startX;
        const dy = touch.clientY - startY;

        // Lock the axis on the first meaningful move: if the gesture is mostly
        // vertical, let the page scroll and ignore it; if mostly horizontal,
        // claim it as a swipe.
        if (!decided) {
          if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
          decided = true;
          dragging = Math.abs(dx) > Math.abs(dy);
          if (dragging) {
            // Pause autoplay for the duration of the drag. Kill any in-flight
            // GSAP tween on the track so it doesn't fight the finger.
            if (this.#autoplay && !this.#paused && !this.#swipePaused) {
              this.#swipePaused = true;
              this.#freezeProgress();
            }
            if (this.#hasGsap) gsap.killTweensOf(this.#track);
            this.#track.classList.add('is-sliding');
          }
        }
        if (!dragging) return;

        // Follow the finger. At a hard edge (no loop) apply rubber-band
        // resistance so the track resists instead of dragging into emptiness.
        let offset = dx;
        const atStart = this.#index === 0;
        const atEnd = this.#index === this.#count - 1;
        if (!this.#loop && ((atStart && dx > 0) || (atEnd && dx < 0))) {
          offset = dx * 0.35;
        }

        // Queue the write; the rAF coalesces multiple moves in one frame into a
        // single DOM mutation.
        pendingPx = basePx + offset;
        if (!rafId) rafId = requestAnimationFrame(flush);

        // We're handling this gesture horizontally; stop the page from scrolling.
        e.preventDefault();
      },
      { passive: false }
    );

    const end = () => {
      if (!dragging) {
        reset();
        return;
      }

      const dx = this.#lastTouchX - startX;
      const elapsed = performance.now() - startTime;
      const velocity = Math.abs(dx) / Math.max(elapsed, 1); // px per ms
      const threshold = width * 0.18;
      const passed = Math.abs(dx) > threshold || velocity > 0.45;

      // Flush any queued frame so the GSAP handoff below starts from the exact
      // pixel position the finger left the track at.
      if (pendingPx !== null) {
        currentPx = pendingPx;
        this.#track.style.transform = `translate3d(${currentPx}px, 0, 0)`;
      }
      const releasedPx = currentPx;
      reset();

      if (passed) {
        const dir = dx < 0 ? 1 : -1;
        this.#animateFromDrag(releasedPx, width, dir);
      } else {
        this.#animateFromDrag(releasedPx, width, 0);
      }
    };

    media.addEventListener('touchend', end);
    media.addEventListener('touchcancel', () => {
      if (dragging) {
        const releasedPx = pendingPx !== null ? pendingPx : currentPx;
        reset();
        this.#animateFromDrag(releasedPx, width, 0);
      } else {
        reset();
      }
    });
  }

  // Hand the track off from a finger-drag (raw pixel transform) back to the
  // slide model. `dir` is -1/0/1: 0 snaps back to the current slide, ±1 commits
  // to the previous/next slide. We tween in pixels from the released position
  // for a seamless continuation, then normalise the transform back to the
  // percent/xPercent basis the rest of the component uses.
  #animateFromDrag(releasedPx, width, dir) {
    let next = this.#index;
    if (dir !== 0) {
      next = this.#index + dir;
      if (this.#loop) next = (next + this.#count) % this.#count;
      else next = Math.max(0, Math.min(this.#count - 1, next));
    }

    const committing = next !== this.#index;
    const targetPx = -next * width;

    const finalise = () => {
      this.#track.classList.remove('is-sliding');
      // Normalise off the temporary pixel transform and onto the shared
      // percent basis so a later resize keeps slides aligned.
      this.#resetTrackToIndex(next);
    };

    if (this.#hasGsap) {
      gsap.fromTo(
        this.#track,
        { x: releasedPx, xPercent: 0 },
        {
          x: targetPx,
          duration: committing ? 0.5 : 0.35,
          ease: 'power3.out',
          onComplete: finalise,
        }
      );
    } else {
      this.#track.style.transition = 'transform 0.35s ease-out';
      this.#track.style.transform = `translate3d(${targetPx}px, 0, 0)`;
      const onEnd = () => {
        this.#track.removeEventListener('transitionend', onEnd);
        this.#track.style.transition = '';
        finalise();
      };
      this.#track.addEventListener('transitionend', onEnd);
    }

    if (committing) {
      // Commit the slide model so panels/dots/colours/reveal update in step
      // with the image, without re-driving the track transform (the handoff
      // tween above already owns it). #goTo re-arms autoplay itself.
      this.#swipePaused = false;
      this.#goTo(next, { driveTrack: false });
    } else if (this.#swipePaused) {
      this.#swipePaused = false;
      if (!this.#paused && !this.#hoverPaused) this.#resumeProgress();
    }
  }

  // Snap the track transform to a slide's resting position using the shared
  // percent basis (matching #goTo), clearing the temporary pixel/x transform.
  #resetTrackToIndex(index) {
    if (this.#hasGsap) {
      gsap.set(this.#track, { x: 0, xPercent: -100 * index });
    } else {
      this.#track.style.transform = `translate3d(${-100 * index}%, 0, 0)`;
    }
  }
}

if (!customElements.get('hero-carousel')) {
  customElements.define('hero-carousel', HeroCarousel);
}
